/**
 * LIBRARY mode (left column): THE clip list — one list, not one per thing you might do with it.
 *
 * Before this pass the same clips were drawn three times (here to organise them, in the right
 * column to download them, in EXPORT to embed them) and every row here was three lines tall with
 * four permanent buttons and a folder `<select>`. Now a row is one line: a tick box, the name, its
 * length, and four actions that appear on hover (`.row .tools`). The tick box is the selection the
 * right column acts on (`librarySelection`), so "which clips" is answered once, in the list.
 *
 * Folders are metadata, not clip fields: the store carries `library.folders` (user folders, never
 * `General`) and a `folder` on every summary. `General` is the one spelling of "unfiled", which is
 * why it is always the first group and has no rename / delete of its own.
 */

import { DEFAULT_FOLDER } from '../../shared';
import type { EditorContext } from '../context';
import type { LibraryClipSummary } from '../types';
import type { Controllers } from '../controllers/types';
import { dialogs } from '../dialogs';
import { button, el, icon, iconButton } from './dom';
import { librarySelection } from './librarySelection';
import {
  confirmDiscardDirtyClip,
  createPanel,
  note,
  runAsync,
  section,
  toolsCell,
  type PanelHandle,
} from './panel';
import './library.css';

function when(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

/**
 * The order the groups are drawn in: `General` first, then the user's folders in creation order,
 * then any folder name that only a clip knows about.
 *
 * That last case should not survive a `refresh()` — `sanitizeFolderState` adopts a folder it finds
 * on an assignment — but rendering from the summaries as well means a clip can never become
 * invisible because the two lists disagreed.
 */
function folderOrder(clips: readonly LibraryClipSummary[], folders: readonly string[]): string[] {
  const order = [DEFAULT_FOLDER, ...folders];
  const seen = new Set(order);
  for (const clip of clips) {
    if (seen.has(clip.folder)) continue;
    seen.add(clip.folder);
    order.push(clip.folder);
  }
  return order;
}

export interface ClipRowOptions {
  /** `pick` = the LIBRARY selection, `embed` = the EXPORT embed list. */
  role: 'pick' | 'embed';
  checked: boolean;
  /** The right-hand `.dim` text — length/fps here, plus "open" for the clip in the editor. */
  meta: string;
  onToggle: () => void;
  /** Double-click action. Omitted where there is nothing to open (the EXPORT embed list). */
  onOpen?: () => void;
  tools?: Array<HTMLElement | null>;
}

/**
 * The one clip-row shape. The LIBRARY list and the EXPORT embed list are the same row with a
 * different tick source, so they share this instead of drifting apart again (audit B22).
 *
 * Clicking the row toggles the tick; only the tools and the box itself are exempt, so the whole
 * 280px width is a target instead of a 13px square.
 */
export function clipRow(clip: LibraryClipSummary, options: ClipRowOptions): HTMLElement {
  const box = el('input', {
    type: 'checkbox',
    'data-role': options.role,
    'data-clip-id': clip.id,
  });
  box.checked = options.checked;
  box.addEventListener('change', () => options.onToggle());

  const tools = (options.tools ?? []).filter((tool): tool is HTMLElement => tool !== null);
  const row = el('div', { class: options.checked ? 'row clip sel' : 'row clip', 'data-clip': clip.id }, [
    box,
    icon('film', 12),
    el('span', { class: 'grow', text: clip.name, title: `updated ${when(clip.updatedAt)}` }),
    el('span', { class: 'dim', text: options.meta }),
    tools.length > 0 ? toolsCell(tools) : null,
  ]);

  row.addEventListener('click', (event) => {
    const target = event.target;
    // The box fires its own `change`; the tools are their own actions.
    if (target instanceof HTMLInputElement) return;
    if (target instanceof Element && target.closest('.tools')) return;
    /*
     * `detail` is the platform's click count, not something read off the DOM — which is what makes
     * the double click survive the rebuild the FIRST click schedules. A `dblclick` listener on a
     * row element that no longer exists by the second click would never fire.
     */
    if (options.onOpen && event.detail >= 2) {
      // The first click of the pair already toggled the tick. Opening a clip is not selecting it,
      // so that toggle is undone here and the row goes back to the state it was double-clicked in.
      options.onToggle();
      options.onOpen();
      return;
    }
    options.onToggle();
  });

  return row;
}

export function mountLibraryPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  /** The library in display order — the order a batch action has to arrive in. */
  function orderedClips(): LibraryClipSummary[] {
    const state = store.getState();
    const clips = state.library.clips;
    const rows: LibraryClipSummary[] = [];
    for (const folder of folderOrder(clips, state.library.folders)) {
      for (const clip of clips) if (clip.folder === folder) rows.push(clip);
    }
    return rows;
  }

  // --- the questions this panel asks -----------------------------------------------------------

  async function openClip(clip: LibraryClipSummary): Promise<void> {
    if (!(await confirmDiscardDirtyClip(store))) return;
    await controllers.library.loadClip(clip.id);
  }

  async function newClip(): Promise<void> {
    // The unsaved-work question comes FIRST: asking for a name and then throwing the typed name
    // away because the user kept the dirty clip is work done twice for nothing.
    if (!(await confirmDiscardDirtyClip(store))) return;
    const name = await dialogs.text({
      title: 'New clip',
      label: 'Name',
      value: 'new clip',
      confirmLabel: 'Create',
    });
    if (!name) return;
    // The question has been asked here, in the one wording (`confirmDiscardDirtyClip`); the
    // controller's own `window.confirm` fallback must not ask it again.
    controllers.library.newClip(name, { confirm: () => true });
  }

  async function renameClip(clip: LibraryClipSummary): Promise<void> {
    const name = await dialogs.text({
      title: 'Rename clip',
      label: 'Name',
      value: clip.name,
      confirmLabel: 'Rename',
    });
    if (!name || name === clip.name) return;
    await controllers.library.renameClip(clip.id, name);
  }

  async function deleteClip(clip: LibraryClipSummary): Promise<void> {
    const ok = await dialogs.confirm({
      title: 'Delete clip',
      message: `Delete clip "${clip.name}" from the library?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) await controllers.library.deleteClip(clip.id);
  }

  async function newFolder(): Promise<void> {
    const name = await dialogs.text({
      title: 'New folder',
      label: 'Name',
      placeholder: 'Locomotion',
      confirmLabel: 'Create',
    });
    if (name) await controllers.library.createFolder(name);
  }

  async function renameFolder(name: string, folders: readonly string[]): Promise<void> {
    const to = await dialogs.text({
      title: 'Rename folder',
      label: 'Name',
      value: name,
      confirmLabel: 'Rename',
    });
    if (!to || to === name) return;
    // The data layer merges onto an existing name silently and there is no undo, so the question
    // is asked here, before anything is written.
    if (folders.includes(to)) {
      const merge = await dialogs.confirm({
        title: 'Merge folders',
        message: `Folder "${to}" exists — merge "${name}" into it?`,
        confirmLabel: 'Merge',
      });
      if (!merge) return;
    }
    await controllers.library.renameFolder(name, to);
  }

  async function deleteFolder(name: string, count: number): Promise<void> {
    const ok = await dialogs.confirm({
      title: 'Delete folder',
      message: `Delete folder "${name}"? Its ${count} clip(s) move to ${DEFAULT_FOLDER}.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) await controllers.library.deleteFolder(name);
  }

  // --- rows ------------------------------------------------------------------------------------

  function libraryClipRow(clip: LibraryClipSummary): HTMLElement {
    const state = store.getState();
    const open = state.clip?.id === clip.id;
    const meta =
      `${clip.durationFrames}f · ${clip.fps}fps` +
      (open ? (state.clipDirty ? ' · open ●' : ' · open') : '');

    return clipRow(clip, {
      role: 'pick',
      checked: librarySelection.has(clip.id),
      meta,
      onToggle: () => librarySelection.toggle(clip.id),
      onOpen: () => runAsync(openClip(clip)),
      tools: [
        iconButton('open', 'Open in editor', () => runAsync(openClip(clip)), {
          'data-action': 'load-clip',
        }),
        iconButton('pencil', 'Rename', () => runAsync(renameClip(clip)), {
          'data-action': 'rename-clip',
        }),
        iconButton('copy', 'Duplicate', () => runAsync(controllers.library.duplicateClip(clip.id)), {
          'data-action': 'duplicate-clip',
        }),
        iconButton('trash', 'Delete', () => runAsync(deleteClip(clip)), {
          'data-action': 'delete-clip',
        }),
      ],
    });
  }

  function folderRow(
    name: string,
    members: readonly LibraryClipSummary[],
    userFolders: readonly string[],
  ): HTMLElement {
    const ids = members.map((clip) => clip.id);
    const isDefault = name === DEFAULT_FOLDER;
    const box = el('input', {
      type: 'checkbox',
      'data-role': 'select-folder',
      title: `Select every clip in ${name}`,
      disabled: ids.length === 0,
    });
    box.checked = ids.length > 0 && ids.every((id) => librarySelection.has(id));
    box.addEventListener('change', () => librarySelection.setMany(ids, box.checked));

    return el('div', { class: 'row folder', 'data-folder': name }, [
      box,
      icon('folder', 12),
      el('span', { class: 'grow', text: name }),
      el('span', { class: 'dim', text: `${members.length}` }),
      toolsCell([
        iconButton(
          'download',
          'Download folder as set',
          () => runAsync(controllers.library.downloadClipSet(ids, `${name}.json`)),
          { 'data-action': 'download-folder', disabled: ids.length === 0 },
        ),
        // `General` is reserved at the data layer (FOLDER_RESERVED). Hiding the two actions is
        // better than letting the user press them and read an error for something that was never
        // possible.
        isDefault
          ? null
          : iconButton('pencil', 'Rename folder', () => runAsync(renameFolder(name, userFolders)), {
              'data-action': 'rename-folder',
            }),
        isDefault
          ? null
          : iconButton('trash', 'Delete folder', () => runAsync(deleteFolder(name, ids.length)), {
              'data-action': 'delete-folder',
            }),
      ]),
    ]);
  }

  function listRows(): HTMLElement[] {
    const state = store.getState();
    const clips = state.library.clips;
    const userFolders = state.library.folders;

    // Nothing at all: one line, no empty `General` header to explain.
    if (clips.length === 0 && userFolders.length === 0) return [note('Library is empty.')];

    const rows: HTMLElement[] = [];
    if (clips.length === 0) rows.push(note('Library is empty.'));

    for (const folder of folderOrder(clips, userFolders)) {
      const members = clips.filter((clip) => clip.folder === folder);
      // An empty *user* folder keeps its header — otherwise a folder created by mistake could
      // never be renamed or deleted again.
      rows.push(folderRow(folder, members, userFolders));
      for (const clip of members) rows.push(libraryClipRow(clip));
    }
    return rows;
  }

  function render(root: HTMLElement): void {
    const clips = store.getState().library.clips;
    const picked = orderedClips().filter((clip) => librarySelection.has(clip.id));
    const meta = `${clips.length} clips${picked.length > 0 ? ` · ${picked.length} selected` : ''}`;

    // The list scrolls inside the block; these three stay put (`.buttons.libhead` is sticky).
    const head = el('div', { class: 'buttons libhead' }, [
      button([icon('plus'), 'New clip'], () => runAsync(newClip()), { 'data-action': 'new-clip' }),
      button([icon('folder'), 'New folder'], () => runAsync(newFolder()), {
        'data-action': 'new-folder',
      }),
      iconButton('refresh', 'Refresh', () => runAsync(controllers.library.refresh())),
    ]);

    root.appendChild(
      section('Clips', [head, ...listRows()], {
        icon: 'film',
        meta,
        key: 'library-clips',
        fill: true,
      }),
    );
  }

  const instance = createPanel(container, ctx, render, {
    className: 'library',
    layout: 'fill',
    slices: [
      (s) => s.library.clips,
      (s) => s.library.folders,
      (s) => s.clipDirty,
      // The open clip by ID: `s.clip` itself changes identity on every pose edit, and this list
      // only cares about *which* clip is open.
      (s) => s.clip?.id,
    ],
  });

  // The ticks are shared with the right column, so they live outside the panel — and they belong
  // to this visit to LIBRARY, so leaving it (this panel's dispose) forgets them.
  instance.own(librarySelection.subscribe(() => instance.schedule()));
  instance.own(() => librarySelection.clear());

  runAsync(controllers.library.refresh());
  return instance;
}
